import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';

const AdminAnalytics = () => {
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, 'users');
        const usersSnap = await getDocs(usersRef);

        const studentsList = [];
        const teachersList = [];
        const adminsList = [];
        const activeList = [];

        const now = new Date();
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 7);

        usersSnap.forEach((docSnap) => {
          const user = docSnap.data();
          const lastLogin = user.lastLoginAt?.toDate
            ? user.lastLoginAt.toDate()
            : user.lastLoginAt?.seconds
            ? new Date(user.lastLoginAt.seconds * 1000)
            : null;

          const userItem = {
            id: docSnap.id,
            name: user.name || 'Unnamed',
            email: user.email || 'No Email',
            lastLogin: lastLogin ? lastLogin.toLocaleDateString() : 'Never',
            role: user.role || 'student',
          };

          if (user.role === 'teacher') {
            teachersList.push(userItem);
          } else if (user.role === 'admin') {
            adminsList.push(userItem);
          } else {
            studentsList.push(userItem);
          }

          if (lastLogin && lastLogin >= sevenDaysAgo) {
            activeList.push(userItem);
          }
        });

        setStudents(studentsList);
        setTeachers(teachersList);
        setAdmins(adminsList);
        setActiveUsers(activeList);
      } catch (error) {
        console.error('❌ Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleViewStudent = (userId) => navigate(`/admin/student/${userId}`);

  if (loading) return <div style={styles.loading}>Loading analytics...</div>;

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <button onClick={() => navigate('/admin')} style={styles.backButton}>← Back</button>
        <h1 style={styles.title}>Platform Analytics</h1>
      </header>

      {/* 📊 KPI CARDS */}
      <div style={styles.kpiGrid}>
        <div style={{ ...styles.kpiCard, borderLeft: '5px solid #10b981' }}>
          <span style={styles.kpiLabel}>Weekly Active Users</span>
          <span style={styles.kpiValue}>{activeUsers.length}</span>
        </div>
        <div style={styles.kpiCard}>
          <span style={styles.kpiLabel}>Total Students</span>
          <span style={styles.kpiValue}>{students.length}</span>
        </div>
        <div style={styles.kpiCard}>
          <span style={styles.kpiLabel}>Total Teachers</span>
          <span style={styles.kpiValue}>{teachers.length}</span>
        </div>
      </div>

      <div style={styles.mainGrid}>
        {/* ACTIVE USERS LIST */}
        <section style={styles.listSection}>
          <h2 style={styles.sectionTitle}>🟢 Recently Active (7 Days)</h2>
          <div style={styles.listContainer}>
            {activeUsers.length === 0 ? <p style={styles.dimText}>No recent activity.</p> : 
              activeUsers.map(user => (
                <div key={user.id} style={styles.userRow}>
                  <div>
                    <div style={styles.userName}>{user.name}</div>
                    <div style={styles.userEmail}>{user.role.toUpperCase()} • {user.email}</div>
                  </div>
                  <div style={styles.userDate}>{user.lastLogin}</div>
                </div>
              ))
            }
          </div>
        </section>

        {/* STUDENT ROSTER */}
        <section style={styles.listSection}>
          <h2 style={styles.sectionTitle}>🎓 Student Roster</h2>
          <div style={styles.listContainer}>
            {students.map(user => (
              <div key={user.id} style={styles.userRow}>
                <div>
                  <div style={styles.userName}>{user.name}</div>
                  <div style={styles.userEmail}>{user.email}</div>
                </div>
                <button style={styles.viewButton} onClick={() => handleViewStudent(user.id)}>
                  View Progress
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '40px 20px', maxWidth: '1200px', margin: '0 auto', backgroundColor: '#f8fafc', minHeight: '100vh' },
  header: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px' },
  backButton: { padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer' },
  title: { fontSize: '2rem', color: '#1e3a8a', fontFamily: 'Merriweather, serif', margin: 0 },
  loading: { textAlign: 'center', marginTop: '100px', fontSize: '1.2rem', color: '#64748b' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '40px' },
  kpiCard: { backgroundColor: '#fff', padding: '25px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' },
  kpiLabel: { color: '#64748b', fontSize: '0.85rem', fontWeight: 'bold', textTransform: 'uppercase' },
  kpiValue: { fontSize: '2.2rem', fontWeight: '800', color: '#1e3a8a' },
  mainGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '30px' },
  listSection: { backgroundColor: '#fff', padding: '25px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' },
  sectionTitle: { fontSize: '1.25rem', color: '#1e3a8a', marginBottom: '20px', borderBottom: '2px solid #f1f5f9', paddingBottom: '10px' },
  listContainer: { maxHeight: '400px', overflowY: 'auto' },
  userRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' },
  userName: { fontWeight: 'bold', color: '#334155' },
  userEmail: { fontSize: '0.8rem', color: '#64748b' },
  userDate: { fontSize: '0.85rem', color: '#94a3b8' },
  viewButton: { padding: '6px 12px', backgroundColor: '#eff6ff', color: '#1e40af', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' },
  dimText: { color: '#94a3b8', fontStyle: 'italic' }
};

export default AdminAnalytics;